import { ArrowUpRight, ArrowRight } from "lucide-react";
import { SectionHead, Stagger, StaggerItem } from "@/components/Reveal";

const PRODUCTS = [
  {
    name: "Skillbuddy",
    desc: "AI-supported learning companion focused on life skills.",
    points: ["Money, communication and career skills", "Learns through conversation and practice", "Personalized paths, judgment-free feedback"],
    href: "https://www.skillbuddy.net",
    page: "/skillbuddy",
    accent: "cyan",
    logo: null,
    testId: "skillbuddy",
  },
  {
    name: "PitchPrep",
    desc: "Prepare, practise and refine your investor pitch.",
    points: ["Structured pitch practice sessions", "Blunt, usable feedback on your story", "Drills for tough investor questions"],
    href: "https://www.pitchprep.co.in",
    page: "/pitchprep",
    accent: "orange",
    logo: "/assets/pitchprep-wordmark.png",
    testId: "pitchprep",
  },
];

export default function ProductsSection() {
  return (
    <section id="products" data-testid="products-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHead
          overline="Our Products"
          title="Products Built to Solve Real-World Challenges"
          sub="Alongside client work, we build our own products. Both are currently in pilot — join early and help shape what they become."
          testId="products-heading"
        />
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          {PRODUCTS.map((p) => (
            <StaggerItem key={p.name}>
              <div
                data-testid={`product-card-${p.testId}`}
                className={`group flex h-full flex-col rounded-2xl border bg-nexlight p-8 sm:p-10 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] ${
                  p.accent === "cyan" ? "border-nexblue/10 hover:border-nexcyan/50" : "border-nexblue/10 hover:border-nexorange/60"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  {p.logo ? (
                    <img src={p.logo} alt={`${p.name} logo`} className="h-10 w-auto" />
                  ) : (
                    <span className="font-display text-2xl font-bold tracking-tight text-nexblue">
                      Skill<span className="text-nexcyan">buddy</span>
                    </span>
                  )}
                  <span className="rounded-full border border-nexorange/40 bg-nexorange/10 px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-nexorange">
                    IN PILOT
                  </span>
                </div>
                <p className="mt-6 text-base sm:text-lg font-medium leading-relaxed text-nexink/85" data-testid={`product-desc-${p.testId}`}>
                  {p.desc}
                </p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5 text-sm text-nexink/70">
                      <span className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${p.accent === "cyan" ? "bg-nexcyan" : "bg-nexorange"}`} />
                      {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-join-pilot-${p.testId}`}
                    className={`group/btn inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-transform duration-300 hover:scale-[1.04] ${
                      p.accent === "cyan" ? "bg-nexblue text-white" : "bg-[#E6631F] text-white"
                    }`}
                  >
                    Join the Pilot
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </a>
                  <a
                    href={p.page}
                    data-testid={`product-details-${p.testId}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-nexink/60 transition-colors duration-300 hover:text-nexcyan"
                  >
                    View details
                    <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
